/**
 * PostCarousel Component for LOOPINO AI Mobile
 * Handles image and video carousel with swipe gestures and dots indicator
 */

import React, { useState, useCallback, useRef } from 'react';
import Skeleton from '../../effects/Skeleton';
import { useMobileSwipe } from '../../../hooks/useMobileSwipe';
import useLanguage from '../../../hooks/useLanguage';

const EDGE_GUARD_PX = 24;
const EDGE_GUARD_RATIO = 0.08;
const CENTRAL_ZONE_RATIO = 0.9;
const MIN_CENTRAL_WIDTH = 260;
const BOUNDARY_SWIPE_ACTIVATION_DELTA = 1;

// AI icon for regeneration button - using provided icon-ai.svg
const AIIcon = () => (
  <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819ZM10.6618 6.8685C10.4867 7.36268 10.3008 8.10258 10.0328 9.17365C9.96005 9.46604 9.87841 9.86432 9.63366 10.1819L9.63167 10.1849C9.55326 10.2858 9.46375 10.3776 9.36493 10.4586L9.36393 10.4576C9.05572 10.7115 8.65922 10.8071 8.37359 10.8886L8.37459 10.8896C7.44827 11.1546 6.81882 11.3347 6.39888 11.5017C6.37093 11.5128 6.34557 11.5253 6.32125 11.5355C6.34567 11.5459 6.37079 11.5592 6.39888 11.5704C6.81882 11.7373 7.44826 11.9174 8.37459 12.1825H8.37359C8.62903 12.2552 8.96143 12.3366 9.24648 12.5249L9.36493 12.6125L9.36791 12.6154L9.50925 12.7468C9.55377 12.7931 9.59654 12.8411 9.63565 12.8921H9.63466C9.87752 13.2082 9.95977 13.6057 10.0328 13.8984L10.3821 15.267C10.4841 15.6477 10.5742 15.9565 10.6618 16.2036C10.6741 16.2383 10.6882 16.2699 10.6996 16.3001C10.7111 16.2698 10.7261 16.2384 10.7385 16.2036C10.9136 15.7094 11.0995 14.9695 11.3675 13.8984C11.4402 13.6061 11.522 13.2078 11.7666 12.8902L11.7686 12.8872L11.895 12.7428C11.9381 12.6986 11.9837 12.6568 12.0314 12.6174L12.1508 12.5279C12.4376 12.3366 12.7736 12.2544 13.0257 12.1825L14.2042 11.8391C14.5295 11.74 14.7917 11.6538 15.0014 11.5704C15.0291 11.5593 15.0539 11.5457 15.078 11.5355C15.054 11.5254 15.029 11.5127 15.0014 11.5017C14.7916 11.4183 14.5296 11.3311 14.2042 11.232L13.0257 10.8896C12.7349 10.8066 12.3427 10.7116 12.0354 10.4596L12.0324 10.4566C11.9345 10.3755 11.8462 10.2836 11.7686 10.1829V10.1839C11.5836 9.94538 11.4913 9.65879 11.4262 9.40854L11.3675 9.17365C11.0995 8.10255 10.9136 7.36267 10.7385 6.8685C10.726 6.83333 10.7112 6.80151 10.6996 6.77095C10.6881 6.80138 10.6742 6.8335 10.6618 6.8685ZM2.13993 8.96762V7.68366H0.855972C0.3832 7.68366 0 7.30046 0 6.82769C7.73956e-05 6.35498 0.383248 5.97172 0.855972 5.97172H2.13993V4.68776C2.13993 4.21503 2.52319 3.83186 2.9959 3.83179C3.46867 3.83179 3.85187 4.21499 3.85187 4.68776V5.97172H5.13583C5.60855 5.97172 5.99172 6.35498 5.9918 6.82769C5.9918 7.30046 5.6086 7.68366 5.13583 7.68366H3.85187V8.96762C3.85187 9.44039 3.46867 9.82359 2.9959 9.82359C2.52319 9.82351 2.13993 9.44034 2.13993 8.96762Z" fill="white"/>
  </svg>
);

// Upload icon for image upload button
const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="7,10 12,5 17,10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="5" x2="12" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Helper function to detect if a media URL is a video
const isVideoFile = (url) => {
  if (!url) return false;
  
  // Check by file extension
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
  const urlLowerCase = url.toLowerCase();
  
  return videoExtensions.some(ext => urlLowerCase.includes(ext));
};

// Helper function to get media type
const getMediaType = (url) => {
  return isVideoFile(url) ? 'video' : 'image';
};

const PostCarousel = ({ 
  images = [], // Note: "images" props will now contain both images and videos
  currentIndex = 0, 
  onIndexChange,
  onImageClick,
  onRegenerateImage,
  onUploadImage,
  isRegenerating = false,
  onSwipeStart,
  onSwipeEnd,
  disabled = false,
  isPostActive = true, // New prop to know if the post containing this carousel is active
  isLoading = false,
  postSwipeHandlers = null,
  getImageLoadingProps = null,
  imageLoadingScope = 'carousel'
}) => {
  const { t } = useLanguage();
  const [localIndex, setLocalIndex] = useState(currentIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [tempOffset, setTempOffset] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);
  const [videoPaused, setVideoPaused] = useState({});
  const videoRefs = useRef({});
  const containerRef = useRef(null);
  const delegatedSwipeRef = useRef(false);
  const handoffInitiatedRef = useRef(false);

  const resolveImageLoadingProps = useCallback((url, idx = 'media') => {
    if (typeof getImageLoadingProps !== 'function') {
      return null;
    }

    const suffix = typeof idx === 'number' ? idx : (idx || 'media');
    const scopedKey = `${imageLoadingScope}-${suffix}`;
    return getImageLoadingProps(url, scopedKey);
  }, [getImageLoadingProps, imageLoadingScope]);

  // Update local index when prop changes
  React.useEffect(() => {
    setLocalIndex(currentIndex);
  }, [currentIndex]);

  // Pause all videos when post becomes inactive
  React.useEffect(() => {
    if (!isPostActive) {
      Object.values(videoRefs.current).forEach(video => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    }
  }, [isPostActive]);

  // Pause videos that are not currently active in carousel
  React.useEffect(() => {
    Object.entries(videoRefs.current).forEach(([videoId, video]) => {
      if (video && video.tagName === 'VIDEO') {
        const videoIndex = parseInt(videoId.split('-').pop());
        const isActiveVideo = videoIndex === localIndex && isPostActive;
        
        if (!isActiveVideo && !video.paused) {
          video.pause();
          setVideoPaused(prev => ({ ...prev, [videoId]: true }));
        }
      }
    });
  }, [localIndex, isPostActive]);

  // Cleanup: pause all videos when component unmounts
  React.useEffect(() => {
    return () => {
      Object.values(videoRefs.current).forEach(video => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    };
  }, []);

  // Handle image change
  const handleIndexChange = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= images.length || isTransitioning || disabled) return;
    
    setIsTransitioning(true);
    setLocalIndex(newIndex);
    
    if (onIndexChange) {
      onIndexChange(newIndex);
    }
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [images.length, isTransitioning, onIndexChange, disabled]);

  // Check current position for boundary conditions
  const isAtFirstImage = localIndex === 0;
  const isAtLastImage = localIndex === images.length - 1;

  // Enhanced swipe handlers with boundary checks
  const handleSwipeLeft = useCallback(() => {
    if (disabled) return;
    const nextIndex = localIndex + 1;
    // Only handle swipe left if there's a next image
    if (nextIndex < images.length) {
      handleIndexChange(nextIndex);
    }
  }, [localIndex, images.length, handleIndexChange, disabled]);

  const handleSwipeRight = useCallback(() => {
    if (disabled) return;
    const prevIndex = localIndex - 1;
    // Only handle swipe right if there's a previous image
    if (prevIndex >= 0) {
      handleIndexChange(prevIndex);
    }
  }, [localIndex, handleIndexChange, disabled]);

  // Custom swipe end handler that determines snap direction
  const handleCustomSwipeEnd = useCallback(() => {
    if (disabled || images.length <= 1 || isTransitioning) return;
    
    // Simple snap back animation when swipe ends
    setIsSnapping(true);
    setTempOffset(0);
    
    setTimeout(() => {
      setIsSnapping(false);
    }, 300);
    
    // Call parent swipe end handler
    if (onSwipeEnd) {
      onSwipeEnd();
    }
  }, [disabled, images.length, isTransitioning, onSwipeEnd]);

  // Initialize swipe hook - always enable when multiple images
  const swipeProps = useMobileSwipe(
    handleSwipeLeft,
    handleSwipeRight,
    {
      threshold: window.innerWidth * 0.25, // 25% of screen width
      velocityThreshold: 0.5,
      onSwipeStart,
      onSwipeEnd: handleCustomSwipeEnd,
      disabled: disabled || images.length <= 1,
      resistanceStiffness: 0.3,
      preventDefaultTouchmove: false
    }
  );
  const resetCarouselSwipe = swipeProps.reset;

  // Track touch start position
  const touchStartRef = useRef({ x: 0, y: 0 });

  const getTouchZone = useCallback((clientX) => {
    const container = containerRef.current;
    if (!container) return 'center';

    const rect = container.getBoundingClientRect();
    const rawThreshold = Math.max(EDGE_GUARD_PX, rect.width * EDGE_GUARD_RATIO);
    const threshold = Math.min(rawThreshold, rect.width / 2);
    const relativeX = clientX - rect.left;

    const centralWidth = Math.min(
      rect.width * CENTRAL_ZONE_RATIO,
      Math.max(rect.width - 2 * threshold, MIN_CENTRAL_WIDTH)
    );

    const centralStart = (rect.width - centralWidth) / 2;
    const centralEnd = centralStart + centralWidth;

    if (relativeX >= centralStart && relativeX <= centralEnd) {
      return 'center';
    }

    return 'edge';
  }, []);

  // Custom touch handlers that allow parent swipes near carousel edges
  const handleTouchStart = useCallback((e) => {
    if (disabled) return;

    const touch = e.touches[0];
    const { clientX, clientY } = touch;
    delegatedSwipeRef.current = false;
    handoffInitiatedRef.current = false;

    if (images.length <= 1) {
      delegatedSwipeRef.current = true;
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchStart?.(e);
      }
      return;
    }

    const zone = getTouchZone(clientX);
    delegatedSwipeRef.current = zone === 'edge';

    if (delegatedSwipeRef.current) {
      handoffInitiatedRef.current = true;
      if (postSwipeHandlers) {
        postSwipeHandlers.reset?.();
        postSwipeHandlers.onTouchStart?.(e);
      }
      return;
    }

    touchStartRef.current = { x: clientX, y: clientY };
    e.stopPropagation();
    swipeProps.onTouchStart(e);
  }, [disabled, images.length, getTouchZone, swipeProps.onTouchStart, postSwipeHandlers]);

  const handleTouchMove = useCallback((e) => {
    if (disabled) return;

    if (images.length <= 1) {
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchMove?.(e);
      }
      return;
    }

    if (delegatedSwipeRef.current) {
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchMove?.(e);
      }
      return;
    }

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;

    // Block invalid movements at boundaries
    if (isAtFirstImage && deltaX > BOUNDARY_SWIPE_ACTIVATION_DELTA) {
      // At first image, block swipe right (positive deltaX = moving right)
      delegatedSwipeRef.current = true;
      if (!handoffInitiatedRef.current) {
        handoffInitiatedRef.current = true;
        resetCarouselSwipe();
        if (postSwipeHandlers) {
          postSwipeHandlers.reset?.();
          postSwipeHandlers.onTouchStart?.(e);
        }
      }
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchMove?.(e);
      }
      return;
    }

    if (isAtLastImage && deltaX < -BOUNDARY_SWIPE_ACTIVATION_DELTA) {
      // At last image, block swipe left (negative deltaX = moving left)
      delegatedSwipeRef.current = true;
      if (!handoffInitiatedRef.current) {
        handoffInitiatedRef.current = true;
        resetCarouselSwipe();
        if (postSwipeHandlers) {
          postSwipeHandlers.reset?.();
          postSwipeHandlers.onTouchStart?.(e);
        }
      }
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchMove?.(e);
      }
      return;
    }

    e.stopPropagation();
    swipeProps.onTouchMove(e);
  }, [disabled, images.length, isAtFirstImage, isAtLastImage, swipeProps.onTouchMove, resetCarouselSwipe, postSwipeHandlers]);

  const handleTouchEnd = useCallback((e) => {
    if (disabled) {
      delegatedSwipeRef.current = false;
      handoffInitiatedRef.current = false;
      return;
    }

    if (images.length <= 1) {
      delegatedSwipeRef.current = false;
      handoffInitiatedRef.current = false;
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchEnd?.(e);
      }
      return;
    }

    if (delegatedSwipeRef.current) {
      if (postSwipeHandlers) {
        postSwipeHandlers.onTouchEnd?.(e);
      }
      delegatedSwipeRef.current = false;
      handoffInitiatedRef.current = false;
      return;
    }

    e.stopPropagation();
    swipeProps.onTouchEnd(e);
    delegatedSwipeRef.current = false;
    handoffInitiatedRef.current = false;
  }, [disabled, images.length, swipeProps.onTouchEnd, postSwipeHandlers]);

  // Apply custom touch events when multiple images and not disabled
  const touchEventProps = !disabled ? {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd
  } : {};

  // Handle image click
  const handleImageClick = (imageIndex) => {
    if (disabled || !onImageClick) return;
    onImageClick(images[imageIndex], imageIndex);
  };

  // Handle AI regenerate click
  const handleRegenerateClick = (e, imageIndex) => {
    e.stopPropagation(); // Prevent image click
    if (disabled || !onRegenerateImage) return;
    onRegenerateImage(imageIndex);
  };

  // Handle upload click
  const handleUploadClick = (e, imageIndex) => {
    e.stopPropagation(); // Prevent image click
    if (disabled || !onUploadImage) return;
    onUploadImage(imageIndex);
  };

  // Handle dot click
  const handleDotClick = (index) => {
    if (disabled) return;
    handleIndexChange(index);
  };

  // Loading skeleton UI for carousel
  if (isLoading) {
    return (
      <div className="mobile-post-image-container">
        <Skeleton variant="rounded" width="100%" height="100%" shimmer />
        <div className="mobile-carousel-indicators" style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Skeleton variant="circle" width="8px" height="8px" shimmer />
          <Skeleton variant="circle" width="8px" height="8px" shimmer />
          <Skeleton variant="circle" width="8px" height="8px" shimmer />
        </div>
      </div>
    );
  }

  // If no media, show placeholder
  if (!images || images.length === 0) {
    return (
      <div className="mobile-post-image-container">
        <div className="mobile-post-empty-image" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: '14px'
        }}>
          {t('postReview.carousel.empty')}
        </div>
      </div>
    );
  }

  // Calculate which images to render for smooth sliding (current + adjacent)
  const getImagesToRender = () => {
    const imagesToRender = [];
    
    // Always render previous, current, and next image for smooth transitions
    for (let i = localIndex - 1; i <= localIndex + 1; i++) {
      if (i >= 0 && i < images.length) {
        imagesToRender.push({
          url: images[i],
          index: i,
          isActive: i === localIndex
        });
      }
    }
    
    return imagesToRender;
  };

  // Render individual media slide (image or video)
  const renderMediaSlide = ({ url, index, isActive }) => {
    // Calculate position with swipe offset for live preview
    const baseOffset = (index - localIndex) * 100;
    
    // Use tempOffset during snapping, otherwise use swipe offset
    let swipeOffsetPercent = 0;
    if (isSnapping) {
      swipeOffsetPercent = tempOffset;
    } else if (images.length > 1 && !disabled && swipeProps.isDragging) {
      swipeOffsetPercent = (swipeProps.swipeOffset / window.innerWidth) * 100;
    }
    
    const totalOffset = baseOffset + swipeOffsetPercent;
    const mediaType = getMediaType(url);

    // Render video or image based on media type
    const renderMediaElement = () => {
      if (mediaType === 'video') {
        const videoId = `video-${index}`;
        const isPaused = videoPaused[videoId] || false;
        const isActiveVideoInCarousel = index === localIndex && isPostActive;
        
        return (
          <div className="mobile-video-container">
            <video 
              ref={el => { 
                if (el) {
                  videoRefs.current[videoId] = el;
                } else {
                  delete videoRefs.current[videoId];
                }
              }}
              id={videoId}
              src={url}
              className="mobile-post-video"
              autoPlay={isActiveVideoInCarousel}
              loop
              playsInline
              muted={false}
              onClick={(e) => {
                e.stopPropagation();
                const video = e.target;
                if (video.paused) {
                  // Only allow play if this video is active in carousel and post is active
                  if (isActiveVideoInCarousel) {
                    video.play();
                    setVideoPaused(prev => ({ ...prev, [videoId]: false }));
                  }
                } else {
                  video.pause();
                  setVideoPaused(prev => ({ ...prev, [videoId]: true }));
                }
              }}
              onPlay={() => setVideoPaused(prev => ({ ...prev, [videoId]: false }))}
              onPause={() => setVideoPaused(prev => ({ ...prev, [videoId]: true }))}
              onLoadedData={(e) => {
                // Auto-pause if not the active video
                if (!isActiveVideoInCarousel && !e.target.paused) {
                  e.target.pause();
                }
              }}
              onError={(e) => {
                console.error('Video load error:', e);
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              {t('postReview.carousel.videoUnsupported')}
            </video>
            
            {/* Play/Pause overlay icon */}
            {isPaused && (
              <div className="mobile-video-play-overlay">
                <div className="mobile-video-play-icon">▶</div>
              </div>
            )}
          </div>
        );
      } else {
        const blurProps = resolveImageLoadingProps(url, index);
        return (
          <img 
            src={url}
            alt={`Post image ${index + 1}`}
            className={[
              'mobile-post-image',
              blurProps ? 'mobile-image-with-loading' : '',
              blurProps?.className || ''
            ].filter(Boolean).join(' ')}
            onClick={() => handleImageClick(index)}
            onLoad={(event) => {
              blurProps?.onLoad?.(event);
            }}
            onError={(e) => {
              blurProps?.onError?.(e);
              e.target.onerror = null;
              // Fallback to placeholder with correct 4:5 aspect ratio
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgdmlld0JveD0iMCAwIDQwMCA1MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNTAwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iNDAiIHk9IjE2MCIgd2lkdGg9IjMyMCIgaGVpZ2h0PSIxODAiIHJ4PSIxMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMiIvPgo8Y2lyY2xlIGN4PSIxMjAiIGN5PSIyMDAiIHI9IjE1IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiLz4KPHBhdGggZD0ibTMyMCAyODAtODAtODAtNTAgNTAtMzAtMzAiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
            }}
            loading="lazy"
            decoding="async"
          />
        );
      }
    };

    return (
      <div
        key={`media-${index}`}
        className={`mobile-carousel-image-slide ${isActive ? 'active' : ''} ${swipeProps.isDragging ? 'dragging' : ''} ${isSnapping ? 'snapping' : ''}`}
        style={{
          transform: `translateX(${totalOffset}%)`,
          transition: (swipeProps.isDragging && !isSnapping) ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {renderMediaElement()}
      </div>
    );
  };

  return (
    <>
      <div 
        ref={containerRef}
        className={`mobile-post-image-container ${disabled ? 'disabled' : ''}`}
        {...touchEventProps}
      >
        {/* Images Slides Container */}
        <div className="mobile-carousel-images-container">
          {images.length === 1 ? (
            // Single media - no sliding needed
            <div className="mobile-carousel-image-slide active">
              {getMediaType(images[0]) === 'video' ? (
                <div className="mobile-video-container">
                  <video 
                    ref={el => { 
                      if (el) {
                        videoRefs.current['video-single'] = el;
                      } else {
                        delete videoRefs.current['video-single'];
                      }
                    }}
                    id="video-single"
                    src={images[0]}
                    className="mobile-post-video"
                    autoPlay={isPostActive}
                    loop
                    playsInline
                    muted={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      const video = e.target;
                      if (video.paused) {
                        // Only allow play if post is active
                        if (isPostActive) {
                          video.play();
                          setVideoPaused(prev => ({ ...prev, 'video-single': false }));
                        }
                      } else {
                        video.pause();
                        setVideoPaused(prev => ({ ...prev, 'video-single': true }));
                      }
                    }}
                    onPlay={() => setVideoPaused(prev => ({ ...prev, 'video-single': false }))}
                    onPause={() => setVideoPaused(prev => ({ ...prev, 'video-single': true }))}
                    onLoadedData={(e) => {
                      // Auto-pause if post is not active
                      if (!isPostActive && !e.target.paused) {
                        e.target.pause();
                      }
                    }}
                    onError={(e) => {
                      console.error('Video load error:', e);
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('postReview.carousel.videoUnsupported')}
                  </video>
                  
                  {/* Play/Pause overlay icon */}
                  {(videoPaused['video-single'] || false) && (
                    <div className="mobile-video-play-overlay">
                      <div className="mobile-video-play-icon">▶</div>
                    </div>
                  )}
                </div>
              ) : (
                (() => {
                  const singleBlurProps = resolveImageLoadingProps(images[0], 0);
                  return (
                    <img 
                      src={images[0]}
                      alt={t('postReview.carousel.images.alt')}
                      className={[
                        'mobile-post-image',
                        singleBlurProps ? 'mobile-image-with-loading' : '',
                        singleBlurProps?.className || ''
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleImageClick(0)}
                      onLoad={(event) => {
                        singleBlurProps?.onLoad?.(event);
                      }}
                      onError={(e) => {
                        singleBlurProps?.onError?.(e);
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgdmlld0JveD0iMCAwIDQwMCA1MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNTAwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iNDAiIHk9IjE2MCIgd2lkdGg9IjMyMCIgaGVpZ2h0PSIxODAiIHJ4PSIxMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMiIvPgo8Y2lyY2xlIGN4PSIxMjAiIGN5PSIyMDAiIHI9IjE1IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiLz4KPHBhdGggZD0ibTMyMCAyODAtODAtODAtNTAgNTAtMzAtMzAiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  );
                })()
              )}
            </div>
          ) : (
            // Multiple media - sliding carousel
            getImagesToRender().map(renderMediaSlide)
          )}
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="mobile-carousel-indicators">
        {images.map((_, index) => (
          <div 
            key={index}
            className={`mobile-indicator-dot ${index === localIndex ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleDotClick(index)}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={t('postReview.carousel.indicators.goTo', { index: index + 1 })}
            onKeyDown={(e) => {
              if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                handleDotClick(index);
              }
            }}
          />
        ))}
      </div>
    </>
  );
};

export default PostCarousel;
